import { ColDef, ColGroupDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-material.css";
import { styled, Box } from "@mui/material";
import Loading from "Components/Loading/Loading";
import NoData from "Components/NoData";
import { useCallback, useEffect, useRef } from "react";

interface IDataTableStyles {
  viewportBorderColor?: string;
  pinnedBorderColor?: string;
}

const StyledContainer = styled(Box)<IDataTableStyles>(
  ({ theme, viewportBorderColor, pinnedBorderColor }) => ({
    height: "100%",
    "& .ag-theme-material": {
      "--ag-font-family": theme.typography.fontFamily,
      "--ag-material-primary-color": theme.palette.primary.main,
      "--ag-background-color": "transparent",
      "--ag-header-background-color": "transparent",
      "--ag-data-color": theme.palette.text.primary,
      "--ag-border-color": theme.palette.divider,
      "--ag-viewport-border-color":
        viewportBorderColor || theme.palette.divider,
      "--ag-pinned-border-color": pinnedBorderColor || theme.palette.divider,
      "--ag-row-border-color": theme.palette.divider,
      "--ag-secondary-foreground-color": theme.palette.text.secondary,
    },

    "& .ag-floating-bottom": {
      overflowY: "hidden!important",
    },
    "& .ag-horizontal-left-spacer": {
      height: 0,
    },
    "& .ag-header-cell": {
      borderRight: `1px solid var(--ag-border-color)`,
    },
    "& .ag-ltr .ag-cell": {
      borderRight: `1px solid var(--ag-border-color)`,
      "& > .MuiStack-root": {
        justifyContent: "center",
      },
    },
    "& .ag-pinned-left-header": {
      borderRight: `1px solid var(--ag-border-color)`,
    },
    "& .ag-header-group-cell": {
      borderRight: `1px solid var(--ag-border-color)`,
      justifyContent: "center",
    },
    "& .ag-paging-panel": {
      justifyContent: "flex-start",
    },
    "& .ag-paging-page-size": {
      display: "none",
    },
    "& .ag-cell-wrapper": {
      height: "100%",
    },
    "& .ag-header-viewport .ag-header-cell-label": {
      justifyContent: "center",
      textAlign: "center",
    },
    "& .ag-header-cell.ag-header-align-left .ag-header-cell-label": {
      justifyContent: "flex-start",
      textAlign: "left",
    },
    "& .ag-pinned-left-header .ag-header-cell-label": {
      justifyContent: "flex-start",
      textAlign: "left",
    },
    "& .ag-pinned-left-cols-container": {
      "& .ag-cell": {
        borderRight: `1px solid var(--ag-pinned-border-color)`,
      },
    },
    "& .ag-center-cols-viewport": {
      "& .ag-row": {
        borderBottom: `1px solid var(--ag-viewport-border-color)`,
      },
      "& .ag-row.ag-row-last": {
        borderBottom: `1px solid var(--ag-border-color)`,
      },
      "& .ag-cell": {
        borderRight: `1px solid var(--ag-viewport-border-color)`,
      },
    },

    [theme.breakpoints.down("md")]: {
      "& .ag-cell-value.ag-column-first .MuiTypography-root": {
        fontSize: 12,
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2, // clamp to 2 lines
        overflow: "hidden",
        textOverflow: "ellipsis",
        textWrap: "auto",
        overflowWrap: "break-word",
      },
    },
  }),
);

interface IDataTableProps {
  colDefs: (ColDef<any, any> | ColGroupDef<any>)[];
  defaultColDef?: ColDef<any, any> | ColGroupDef<any>;
  rows: any[];
  totalRow?: any[];
  pageSize?: number;
  styles?: IDataTableStyles;
  onVisibleRowsChange?: (rows: any[]) => void;
}

function DataTable({
  colDefs,
  rows,
  defaultColDef,
  totalRow,
  pageSize,
  styles,
  onVisibleRowsChange,
}: IDataTableProps) {
  const gridRef = useRef<AgGridReact>(null);
  const rowsRef = useRef(rows);
  const onVisibleRowsChangeRef = useRef(onVisibleRowsChange);
  rowsRef.current = rows;
  onVisibleRowsChangeRef.current = onVisibleRowsChange;

  const emitVisibleRows = useCallback(() => {
    const onChange = onVisibleRowsChangeRef.current;
    if (!onChange) {
      return;
    }
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    const page = api.paginationGetCurrentPage();
    const pageSizeValue = api.paginationGetPageSize();
    const start = page * pageSizeValue;
    onChange(rowsRef.current.slice(start, start + pageSizeValue));
  }, []);

  useEffect(() => {
    emitVisibleRows();
  }, [rows, emitVisibleRows]);

  return (
    //  wrapping container with theme & size
    <StyledContainer {...styles}>
      <div
        className="ag-theme-material" // applying the grid theme
        style={{
          height: "100%",
          width: "100%",
        }}
      >
        <AgGridReact
          ref={gridRef}
          theme={"legacy" as any}
          rowData={rows}
          columnDefs={colDefs}
          onGridReady={emitVisibleRows}
          onPaginationChanged={emitVisibleRows}
          defaultColDef={
            defaultColDef || {
              wrapHeaderText: true,
              flex: 1,
              autoHeaderHeight: true,
              sortable: true,
              // autoHeight: true, // commented out as diabled column virtulisation (only rendering columns in view)
            }
          }
          pinnedBottomRowData={totalRow}
          pagination
          paginationPageSize={pageSize || 10}
          paginationAutoPageSize={pageSize === 0}
          paginationPageSizeSelector={false}
          loadingOverlayComponent={Loading}
          noRowsOverlayComponent={NoData}
          onSortChanged={(params) => {
            params.api.paginationGoToFirstPage();
          }}
          suppressCellFocus
        />
      </div>
    </StyledContainer>
  );
}

export default DataTable;
